import { Mon, MonMetadata, MoveClass } from '../generated/mon';
import { TOTAL_FACETS } from '../data/facets';
import { TYPE_EFFECTIVENESS_CHART } from '../data/types';
import { Type } from '../types/structs';
import { TEAM_SIZE } from '../types/constants';

/**
 * How a random-CPU opponent's TEAM is generated. `pickStrategy` rolls one of these per battle (see the
 * `*_THRESHOLD`s); `buildCpuOpponentTeam` then builds it. Outcomes:
 *   - `random`  — distinct random mons, with a small (RANDOM_REPEAT_BIAS) chance per slot to repeat an
 *                 earlier pick, so a team occasionally has a duplicate or two. Random facets.
 *   - `stacked` — ONE random mon filling all 6 slots (the "all-duplicates" team). Random facets.
 *   - `counter` — the best type-counter to each of the player's mons (resist their moves + hit back),
 *                 with the Atk+/Speed- facet.
 *   - `mirror`  — the PLAYER's own team, copied, with speed-boosting facets (MIRROR_FACET_CHOICES, all
 *                 Speed+). Pairs with the `mirror` MOVE strategy (cpu/strategies/mirror-cpu.ts): the CPU
 *                 plays the player's own move a beat behind but outspeeds it — "fight yourself, faster".
 */
export type CpuTeamStrategy = 'random' | 'counter' | 'mirror' | 'stacked';

export interface CpuTeamBuild {
  readonly strategy: CpuTeamStrategy;
  readonly monIndices: bigint[];
  readonly facetIds: number[];
}

type Rng = () => number;

const MIRROR_THRESHOLD = 1 / 6;
const COUNTER_THRESHOLD = 2 / 6;
// Stacked (4 identical mons) is a rare novelty at 3%. Mirror/counter stay at
// 1/6 each; the freed mass rolls into `random`.
const STACKED_THRESHOLD = COUNTER_THRESHOLD + 0.03;

// Atk+/Speed-. Only facet that fits the "boost offense, give up speed" brief.
const COUNTER_FACET_ID = 6;
// Speed+ facets that don't nerf an offensive stat:
// 10 = Meteoric (Speed+ / HP-), 12 = Unfettered (Speed+ / Def-).
// Skip 11 (Elusive, Speed+ / Atk-) — its Atk nerf drops both physical and
// special attack, kneecapping any offensive mon.
const MIRROR_FACET_CHOICES = [10, 12] as const;

const RANDOM_REPEAT_BIAS = 0.04;

// Fallback for moves whose power is '?', so the scorer doesn't collapse to
// zero on variable-power moves.
const UNKNOWN_POWER_FALLBACK = 60;

const ALL_MONS: readonly Mon[] = Object.values(MonMetadata) as Mon[];
const ALL_MON_IDS: readonly bigint[] = ALL_MONS.map((m) => BigInt(m.id));
const MON_BY_ID: ReadonlyMap<number, Mon> = new Map(ALL_MONS.map((m) => [m.id, m]));

function randomFacetId(rand: Rng): number {
  return 1 + Math.floor(rand() * TOTAL_FACETS);
}

export function pickStrategy(rand: Rng = Math.random): CpuTeamStrategy {
  const r = rand();
  if (r < MIRROR_THRESHOLD) return 'mirror';
  if (r < COUNTER_THRESHOLD) return 'counter';
  if (r < STACKED_THRESHOLD) return 'stacked';
  return 'random';
}

export function buildCpuOpponentTeam(
  playerMonIds: readonly bigint[],
  rand: Rng = Math.random,
): CpuTeamBuild {
  const haveFullPlayerTeam = playerMonIds.length >= TEAM_SIZE;
  let strategy = pickStrategy(rand);
  if (!haveFullPlayerTeam && (strategy === 'mirror' || strategy === 'counter')) {
    strategy = 'random';
  }
  switch (strategy) {
    case 'mirror':  return buildMirrorTeam(playerMonIds, rand);
    case 'counter': return buildCounterTeam(playerMonIds);
    case 'stacked': return buildStackedTeam(rand);
    default:        return buildRandomTeam(rand);
  }
}

export function buildRandomTeam(rand: Rng = Math.random): CpuTeamBuild {
  const monIndices: bigint[] = [];
  const remaining = ALL_MON_IDS.slice();
  for (let i = 0; i < TEAM_SIZE; i++) {
    if (i > 0 && rand() < RANDOM_REPEAT_BIAS) {
      monIndices.push(monIndices[Math.floor(rand() * i)]);
    } else {
      const idx = Math.floor(rand() * remaining.length);
      monIndices.push(remaining[idx]);
      remaining.splice(idx, 1);
    }
  }
  const facetIds = Array.from({ length: TEAM_SIZE }, () => randomFacetId(rand));
  return { strategy: 'random', monIndices, facetIds };
}

export function buildStackedTeam(rand: Rng = Math.random): CpuTeamBuild {
  const pickedId = ALL_MON_IDS[Math.floor(rand() * ALL_MON_IDS.length)];
  const monIndices = Array.from({ length: TEAM_SIZE }, () => pickedId);
  const facetIds = Array.from({ length: TEAM_SIZE }, () => randomFacetId(rand));
  return { strategy: 'stacked', monIndices, facetIds };
}

export function buildMirrorTeam(
  playerMonIds: readonly bigint[],
  rand: Rng = Math.random,
): CpuTeamBuild {
  const monIndices: bigint[] = [];
  for (let i = 0; i < TEAM_SIZE; i++) {
    monIndices.push(playerMonIds[i % playerMonIds.length]);
  }
  const facetIds = monIndices.map(
    () => MIRROR_FACET_CHOICES[Math.floor(rand() * MIRROR_FACET_CHOICES.length)],
  );
  return { strategy: 'mirror', monIndices, facetIds };
}

export function buildCounterTeam(playerMonIds: readonly bigint[]): CpuTeamBuild {
  const monIndices: bigint[] = [];
  for (let i = 0; i < TEAM_SIZE; i++) {
    const playerMon = MON_BY_ID.get(Number(playerMonIds[i % playerMonIds.length]))!;
    monIndices.push(BigInt(pickBestCounter(playerMon).id));
  }
  const facetIds = Array.from({ length: TEAM_SIZE }, () => COUNTER_FACET_ID);
  return { strategy: 'counter', monIndices, facetIds };
}

// Normalize offense scores so the 50/50 blend with the resistance score
// isn't dominated by one axis (raw offScore can run into the thousands).
function pickBestCounter(playerMon: Mon): Mon {
  const offRaw = ALL_MONS.map((c) => offScore(c, playerMon));
  const maxOff = Math.max(...offRaw, 1e-9);
  let best = ALL_MONS[0];
  let bestScore = -Infinity;
  for (let k = 0; k < ALL_MONS.length; k++) {
    const def = defScore(ALL_MONS[k], playerMon);
    const off = offRaw[k] / maxOff;
    const combined = 0.5 * def + 0.5 * off;
    if (combined > bestScore) {
      bestScore = combined;
      best = ALL_MONS[k];
    }
  }
  return best;
}

// Higher = better at resisting attacker's damaging moves. The immunity
// bonus tips ties toward an outright 0× over a 0.5×.
function defScore(defender: Mon, attacker: Mon): number {
  let score = 0;
  for (const move of attacker.moves) {
    if (move.class !== MoveClass.Physical && move.class !== MoveClass.Special) continue;
    const eff = effectivenessOnMon(move.type as Type, defender);
    score += 1 - eff;
    if (eff === 0) score += 0.5;
  }
  return score;
}

// Higher = bigger expected damage from attacker's best attacking move
// against defender.
function offScore(attacker: Mon, defender: Mon): number {
  let best = 0;
  for (const move of attacker.moves) {
    if (move.class !== MoveClass.Physical && move.class !== MoveClass.Special) continue;
    const power = typeof move.power === 'number' ? move.power : UNKNOWN_POWER_FALLBACK;
    const stab =
      move.type === attacker.type1 || move.type === attacker.type2 ? 1.5 : 1.0;
    const eff = effectivenessOnMon(move.type as Type, defender);
    const atk =
      move.class === MoveClass.Physical
        ? attacker.stats.attack
        : attacker.stats.specialAttack;
    const def =
      move.class === MoveClass.Physical
        ? defender.stats.defense
        : defender.stats.specialDefense;
    const dmg = power * stab * eff * (atk / Math.max(1, def));
    if (dmg > best) best = dmg;
  }
  return best;
}

function effectivenessOnMon(moveType: Type, defender: Mon): number {
  const m1 = TYPE_EFFECTIVENESS_CHART[moveType][defender.type1];
  const m2 =
    defender.type2 != null
      ? TYPE_EFFECTIVENESS_CHART[moveType][defender.type2]
      : 1;
  return m1 * m2;
}
